import { Drawer, Toolbar } from '@mui/material';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavbar } from '../../hooks/useNavbar';
import { searchPages } from '../../utils/page';
import { HideLeftButton } from '../Button/HideLeftButton';
import { NavList } from './NavList';
import { NavSearch } from './NavSearch';

export const navWidth = 280;

const drawerStyle = {
	width: navWidth,
	'& .MuiDrawer-paper': { width: navWidth, boxSizing: 'border-box' },
};

const isSearchShortcut = (event) => (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';

export function Navbar({ isOpen, handleToggle, handleOpen, onNavigate }) {
	const { pageStatus } = useNavbar();
	const navigate = useNavigate();
	const [query, setQuery] = useState('');
	const mobileSearchRef = useRef(null);
	const desktopSearchRef = useRef(null);

	useEffect(() => {
		const focusSearch = (event) => {
			if (!isSearchShortcut(event)) return;
			event.preventDefault();
			handleOpen();
			// Wait for the drawer to open before focusing, whichever one is visible.
			setTimeout(() => {
				const input = [desktopSearchRef.current, mobileSearchRef.current].find((el) => el?.offsetParent);
				input?.focus();
				input?.select();
			}, 0);
		};
		window.addEventListener('keydown', focusSearch);
		return () => window.removeEventListener('keydown', focusSearch);
	}, [handleOpen]);

	const handleNavigate = () => {
		setQuery('');
		onNavigate();
	};

	const openFirstMatch = () => {
		const [first] = searchPages(query);
		if (!first) return;
		navigate(first.path);
		handleNavigate();
	};

	const content = (inputRef) => (
		<>
			<NavSearch inputRef={inputRef} value={query} onChange={setQuery} onSubmit={openFirstMatch} />
			<NavList pageStatus={pageStatus} query={query} onNavigate={handleNavigate} />
		</>
	);

	return (
		<Box component="nav">
			<Drawer
				variant="temporary"
				ModalProps={{ keepMounted: true }}
				sx={{
					display: { xs: 'block', md: 'none' },
					...(isOpen && drawerStyle),
				}}
				open={isOpen}
				onClose={handleToggle}
			>
				<Toolbar sx={{ ml: 'auto', p: 1 }}>
					<HideLeftButton handleClick={handleToggle} />
				</Toolbar>
				{content(mobileSearchRef)}
			</Drawer>
			<Drawer
				variant="persistent"
				sx={{
					display: { xs: 'none', md: 'block' },
					...(isOpen && drawerStyle),
				}}
				open={isOpen}
			>
				<Toolbar onClick={handleToggle} sx={{ cursor: 'pointer' }} />
				{content(desktopSearchRef)}
			</Drawer>
		</Box>
	);
}

Navbar.propTypes = {
	isOpen: PropTypes.bool,
	handleToggle: PropTypes.func,
	handleOpen: PropTypes.func,
	onNavigate: PropTypes.func,
};
